import { createContext, useContext } from "react";

import { useStickyState } from "app/hooks/ui/useStickyState";

/** Which GraphQL explorer to render at `/api`. */
export type ApiExplorer = "apollo" | "graphiql";

/** Default explorer when the user has expressed no preference. */
export const DEFAULT_API_EXPLORER: ApiExplorer = "apollo";

/** localStorage key holding the saved {@link ApiExplorer} preference. */
export const API_EXPLORER_STORAGE_KEY = "api-explorer";

/** The `userData` (type `config`) name under which the preference is persisted. */
export const API_EXPLORER_USER_DATA_NAME = "api_explorer";

const API_EXPLORERS: ApiExplorer[] = ["apollo", "graphiql"];

/** Narrow an arbitrary string to an {@link ApiExplorer}, or `null` if invalid. */
export function parseApiExplorer(
  value: string | null | undefined,
): ApiExplorer | null {
  return API_EXPLORERS.includes(value as ApiExplorer)
    ? (value as ApiExplorer)
    : null;
}

interface ApiExplorerContextValue {
  /** The user's saved preference. */
  apiExplorer: ApiExplorer;
  /** Update the preference: applies it immediately and caches it to localStorage. */
  setApiExplorer(explorer: ApiExplorer): void;
}

const ApiExplorerContext = createContext<ApiExplorerContextValue | null>(null);

/** Props for the ApiExplorerProvider component */
interface Props {
  /** Subtree that can read and update the API explorer preference */
  children: React.ReactNode;
}

/**
 * Owns the API explorer preference for the running app. Unlike the color scheme,
 * this preference only affects what renders at `/api` once React has mounted, so
 * it needs no synchronous boot-time application — `useStickyState` reads
 * localStorage on first render and persists changes, while `ApiExplorerSync`
 * reconciles it with the server.
 */
export function ApiExplorerProvider({ children }: Props) {
  const [apiExplorer, setApiExplorer] = useStickyState<ApiExplorer>(
    DEFAULT_API_EXPLORER,
    API_EXPLORER_STORAGE_KEY,
  );

  return (
    <ApiExplorerContext.Provider value={{ apiExplorer, setApiExplorer }}>
      {children}
    </ApiExplorerContext.Provider>
  );
}

export function useApiExplorer(): ApiExplorerContextValue {
  const context = useContext(ApiExplorerContext);
  if (!context) {
    throw new Error(
      "useApiExplorer must be used within an ApiExplorerProvider",
    );
  }
  return context;
}
